import React from 'react';

import { ICON_NAMES, ITestable } from '@belong/types';
import { COLOURS } from '@belong/themes';
import { Hidden } from '@belong/ui-core';
import { Button } from '../../../actions';

import { PLAN_CARD_TEST_ID } from '../testIds';
import { PlanSelectionStatus } from '../types';
import { CTAWrapper, StyledCopy, StyledIcon } from './PlanCTA.styles';

interface IPlanCTAProps extends ITestable {
  label: string;
  onClick?: (evt: any) => void;
  url?: string;
  className?: string;
  ariaDescribedBy?: string;
  selectionStatus?: PlanSelectionStatus;
  focusTextOnSelect?: boolean;
  planName?: string;
}

export const PlanCTA: React.FC<IPlanCTAProps> = ({
  label,
  url,
  className,
  ariaDescribedBy,
  selectionStatus,
  onClick,
  focusTextOnSelect,
  planName,
  'data-testid': testId
}) => {
  const styledCopyRef = React.createRef<HTMLSpanElement>();
  const buttonVisible = !selectionStatus;

  const statusTextMap: { [key in PlanSelectionStatus]: React.ReactNode } = {
    unavailable: 'Plan not available at current address',
    selected: (
      <>
        <StyledIcon name={ICON_NAMES.TickCircle} size="small" /> Selected {planName?.replace('with fibre', '')}
      </>
    ),
    current: (
      <>
        <StyledIcon name={ICON_NAMES.TickCircle} size="small" /> Current plan
      </>
    )
  };

  return (
    <CTAWrapper data-testid={testId}>
      <Hidden invisible={buttonVisible} aria-hidden={buttonVisible}>
        <StyledCopy as="span" hasColor={COLOURS.BLACK} tabIndex={-1} ref={styledCopyRef}>
          {selectionStatus ? statusTextMap[selectionStatus] : ''}
        </StyledCopy>
      </Hidden>
      <Hidden invisible={!buttonVisible} aria-hidden={!buttonVisible}>
        <Button
          width="full"
          disabled={!buttonVisible}
          onClick={evt => {
            onClick?.(evt);

            // When selecting a different plan if page has already scroll to model section then set the focus to the "Selected" text
            if (focusTextOnSelect && styledCopyRef.current) {
              styledCopyRef.current.focus();
            }
          }}
          variant="secondary"
          href={url}
          withFocusRing
          className={className}
          aria-describedby={ariaDescribedBy}
          data-testid={PLAN_CARD_TEST_ID.button}
        >
          {label}
        </Button>
      </Hidden>
    </CTAWrapper>
  );
};

PlanCTA.displayName = 'PlanCTA';
